import React, { useState, useEffect } from 'react';
import { Building2, Mail, Phone, MapPin, Search, Plus, Edit, Shield, ShieldOff, ChevronDown, ChevronUp, X } from 'lucide-react';
import api from '../services/api';

interface Agency {
  agency_id: number;
  agency_name: string;
  address: string;
  wa_number: string;
  notes: string;
  color_code: string;
  logo_s3_url: string;
  footer_content_1: string;
  footer_content_2: string;
  email: string;
  phone: string;
  status: string;
  city: string;
  country: string;
  commission_rate: number | null;
  payment_terms: string;
  business_registration_number: string;
  gstin: string;
  gst_state: string;
  gst_state_code: string;
  has_portal_access: boolean;
}

const emptyForm = {
  agency_name: '', address: '', wa_number: '', notes: '', color_code: '#1E40AF',
  footer_content_1: '', footer_content_2: '', email: '', phone: '',
  city: '', country: '', commission_rate: '', payment_terms: '',
  business_registration_number: '', gstin: '', gst_state: '', gst_state_code: ''
};

const AgentRegistrations: React.FC = () => {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  // Portal access modal
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [portalForm, setPortalForm] = useState({ email: '', name: '', role: 'owner' });
  const [portalAgencyId, setPortalAgencyId] = useState<number | null>(null);

  useEffect(() => { fetchAgencies(); }, []);

  const fetchAgencies = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/agencies-full');
      setAgencies(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filtered = agencies.filter(a => {
    const q = search.toLowerCase();
    return !q || a.agency_name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q) ||
      a.city?.toLowerCase().includes(q) || a.gstin?.toLowerCase().includes(q) ||
      a.agency_id.toString().includes(q);
  });

  const openCreate = () => {
    setForm(emptyForm);
    setLogo(null);
    setEditingId(null);
    setModalMode('create');
    setShowModal(true);
  };

  const openEdit = (a: Agency) => {
    setForm({
      agency_name: a.agency_name || '', address: a.address || '', wa_number: a.wa_number || '',
      notes: a.notes || '', color_code: a.color_code || '#1E40AF',
      footer_content_1: a.footer_content_1 || '', footer_content_2: a.footer_content_2 || '',
      email: a.email || '', phone: a.phone || '', city: a.city || '', country: a.country || '',
      commission_rate: a.commission_rate?.toString() || '', payment_terms: a.payment_terms || '',
      business_registration_number: a.business_registration_number || '',
      gstin: a.gstin || '', gst_state: a.gst_state || '', gst_state_code: a.gst_state_code || ''
    });
    setLogo(null);
    setEditingId(a.agency_id);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.agency_name.trim()) { alert('Agency name is required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (logo) fd.append('logo', logo);

      if (modalMode === 'create') {
        await api.post('/admin/agencies', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.put(`/admin/agencies/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowModal(false);
      fetchAgencies();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const openGrantPortal = (a: Agency) => {
    setPortalAgencyId(a.agency_id);
    setPortalForm({ email: a.email || '', name: a.agency_name || '', role: 'owner' });
    setShowPortalModal(true);
  };

  const handleGrantPortal = async () => {
    if (!portalForm.email.trim() || !portalForm.name.trim()) { alert('Email and name are required'); return; }
    try {
      const fd = new FormData();
      fd.append('email', portalForm.email);
      fd.append('name', portalForm.name);
      fd.append('role', portalForm.role);
      await api.post(`/admin/agencies/${portalAgencyId}/grant-portal-access`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('Portal access granted. User will receive a temporary password.');
      setShowPortalModal(false);
      fetchAgencies();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokePortal = async (agencyId: number) => {
    if (!window.confirm('Revoke portal access? The user will no longer be able to log in.')) return;
    try {
      await api.post(`/admin/agencies/${agencyId}/revoke-portal-access`);
      alert('Portal access revoked');
      fetchAgencies();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const setField = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="w-full px-6 py-6 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agency Management</h1>
            <p className="text-sm text-gray-500 mt-1">{agencies.length} agencies</p>
          </div>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
            <Plus className="h-4 w-4" /> New Agency
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input type="text" placeholder="Search by name, email, city, GST..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        </div>

        {/* Agency List */}
        <div className="space-y-3">
          {filtered.map(a => (
            <div key={a.agency_id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Row */}
              <div className="flex items-center px-5 py-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedId(expandedId === a.agency_id ? null : a.agency_id)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {a.logo_s3_url ? (
                    <img src={a.logo_s3_url} alt="" className="h-9 w-9 rounded-full object-cover border" />
                  ) : (
                    <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: a.color_code ? (a.color_code.startsWith('#') ? a.color_code : `#${a.color_code}`) : '#6B7280' }}>
                      {a.agency_name?.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 truncate">{a.agency_name}</span>
                      <span className="text-xs text-gray-400">#{a.agency_id}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      {a.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>}
                      {a.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{a.city}{a.country ? `, ${a.country}` : ''}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {a.gstin && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">{a.gstin}</span>}
                  {a.has_portal_access ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Portal ✓</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs">No Portal</span>
                  )}
                  {expandedId === a.agency_id ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === a.agency_id && (
                <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div><span className="text-gray-500 text-xs">Email</span><p className="font-medium">{a.email || '—'}</p></div>
                    <div><span className="text-gray-500 text-xs">Phone</span><p className="font-medium">{a.phone || '—'}</p></div>
                    <div><span className="text-gray-500 text-xs">WhatsApp</span><p className="font-medium">{a.wa_number || '—'}</p></div>
                    <div><span className="text-gray-500 text-xs">Address</span><p className="font-medium">{a.address || '—'}</p></div>
                    <div><span className="text-gray-500 text-xs">Notes</span><p className="font-medium">{a.notes || '—'}</p></div>
                    <div><span className="text-gray-500 text-xs">GSTIN</span><p className="font-medium font-mono">{a.gstin || '—'}</p></div>
                    <div><span className="text-gray-500 text-xs">GST State</span><p className="font-medium">{a.gst_state || '—'}{a.gst_state_code ? ` (${a.gst_state_code})` : ''}</p></div>
                    <div><span className="text-gray-500 text-xs">Commission</span><p className="font-medium">{a.commission_rate ? `${a.commission_rate}%` : '—'}</p></div>
                    <div><span className="text-gray-500 text-xs">Payment Terms</span><p className="font-medium">{a.payment_terms || '—'}</p></div>
                    <div><span className="text-gray-500 text-xs">Biz Reg #</span><p className="font-medium">{a.business_registration_number || '—'}</p></div>
                    <div>
                      <span className="text-gray-500 text-xs">Brand Color</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="h-5 w-5 rounded border" style={{ backgroundColor: a.color_code ? (a.color_code.startsWith('#') ? a.color_code : `#${a.color_code}`) : '#ccc' }}></div>
                        <span className="font-medium font-mono text-xs">{a.color_code || '—'}</span>
                      </div>
                    </div>
                    {a.logo_s3_url && (
                      <div>
                        <span className="text-gray-500 text-xs">Logo</span>
                        <img src={a.logo_s3_url} alt="Logo" className="h-10 mt-1 object-contain" />
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t border-gray-200">
                    <button onClick={() => openEdit(a)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
                      <Edit className="h-3.5 w-3.5" /> Edit
                    </button>
                    {a.has_portal_access ? (
                      <button onClick={() => handleRevokePortal(a.agency_id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700">
                        <ShieldOff className="h-3.5 w-3.5" /> Revoke Portal
                      </button>
                    ) : (
                      <button onClick={() => openGrantPortal(a)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700">
                        <Shield className="h-3.5 w-3.5" /> Grant Portal Access
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No agencies found</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold">{modalMode === 'create' ? 'New Agency' : 'Edit Agency'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Agency Name *</label>
                  <input value={form.agency_name} onChange={e => setField('agency_name', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                  <input value={form.phone} onChange={e => setField('phone', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">WhatsApp</label>
                  <input value={form.wa_number} onChange={e => setField('wa_number', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                  <input value={form.city} onChange={e => setField('city', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                  <input value={form.country} onChange={e => setField('country', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                  <textarea value={form.address} onChange={e => setField('address', e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>

                {/* GST Section */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">GST Information</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">GSTIN</label>
                      <input value={form.gstin} onChange={e => setField('gstin', e.target.value)} placeholder="22AAAAA0000A1Z5" className="w-full px-3 py-2 border rounded-lg text-sm font-mono" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">GST State</label>
                      <input value={form.gst_state} onChange={e => setField('gst_state', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">State Code</label>
                      <input value={form.gst_state_code} onChange={e => setField('gst_state_code', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  </div>
                </div>

                {/* Business Section */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Business Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Business Reg #</label>
                      <input value={form.business_registration_number} onChange={e => setField('business_registration_number', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Commission Rate (%)</label>
                      <input type="number" step="0.01" value={form.commission_rate} onChange={e => setField('commission_rate', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Payment Terms</label>
                      <input value={form.payment_terms} onChange={e => setField('payment_terms', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  </div>
                </div>

                {/* Branding */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Branding</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Brand Color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={form.color_code.startsWith('#') ? form.color_code : `#${form.color_code}`} onChange={e => setField('color_code', e.target.value)} className="h-9 w-12 border rounded cursor-pointer" />
                        <input value={form.color_code} onChange={e => setField('color_code', e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Logo</label>
                      {modalMode === 'edit' && editingId && (() => {
                        const editAgency = agencies.find(a => a.agency_id === editingId);
                        return editAgency?.logo_s3_url ? (
                          <div className="mb-2">
                            <img src={editAgency.logo_s3_url} alt="Current logo" className="h-12 object-contain border rounded p-1" />
                            <p className="text-xs text-gray-400 mt-1">Current logo</p>
                          </div>
                        ) : null;
                      })()}
                      <input type="file" accept="image/*" onChange={e => setLogo(e.target.files?.[0] || null)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                      {logo && <p className="text-xs text-green-600 mt-1">New: {logo.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Footer 1</label>
                      <textarea value={form.footer_content_1} onChange={e => setField('footer_content_1', e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Footer 2</label>
                      <textarea value={form.footer_content_2} onChange={e => setField('footer_content_2', e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
                {saving ? 'Saving...' : modalMode === 'create' ? 'Create Agency' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Portal Access Modal */}
      {showPortalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Grant Portal Access</h3>
            <p className="text-sm text-gray-600 mb-4">The user will receive a temporary password and must change it on first login.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Login Email *</label>
                <input type="email" value={portalForm.email} onChange={e => setPortalForm(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Display Name *</label>
                <input value={portalForm.name} onChange={e => setPortalForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                <select value={portalForm.role} onChange={e => setPortalForm(p => ({ ...p, role: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="agent">Agent</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowPortalModal(false)} className="flex-1 px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
              <button onClick={handleGrantPortal} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">Grant Access</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentRegistrations;
